import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, ShieldCheck, FileText, ArrowRight } from 'lucide-react';

/**
 * INTAKE SUCCESS PAGE
 * Transitionary state confirming the brief is archived and prompting Phase 01 Payment.
 * Frames the transaction as an 'Audit Authorization' to support the consultation model.
 */
const IntakeSuccess = ({ briefId, customerName }) => {
    return (
        <div className="min-h-screen bg-stone-50 flex flex-col items-center justify-center p-6 selection:bg-amber-100">
            <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="max-w-2xl w-full bg-white border border-stone-200 p-8 md:p-16 shadow-sm text-center relative overflow-hidden"
            >
                {/* Subtle background branding */}
                <div className="absolute top-0 right-0 p-8 opacity-[0.03] pointer-events-none text-stone-900">
                    <FileText size={200} />
                </div>

                <div className="flex justify-center mb-10">
                    <motion.div 
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.2 }}
                        className="bg-green-50 p-5 rounded-full"
                    >
                        <CheckCircle2 className="text-green-600" size={48} />
                    </motion.div>
                </div>

                <h2 className="font-serif text-3xl md:text-4xl text-stone-900 mb-4 italic">
                    Strategic Brief <span className="not-italic text-stone-400 font-light">Archived.</span>
                </h2>
                <p className="text-[10px] uppercase tracking-[0.5em] text-amber-700 font-bold mb-10">
                    Node Identifier: {briefId || "PENDING_SYNC"}
                </p>

                <div className="text-left bg-stone-50 border border-stone-100 p-8 mb-10 space-y-5 rounded-sm">
                    <p className="text-stone-600 text-sm leading-relaxed font-light">
                        Principal <strong>{customerName || "Designate"}</strong>, your technical parameters have been successfully ingested into our forensic engine. 
                    </p>
                    <p className="text-stone-600 text-sm leading-relaxed font-light">
                        To proceed with <span className="font-bold text-stone-900">Phase 01: Forensic Infrastructure Audit</span>, you must now authorize the technical review fee. This covers the manual auditing of your data and grants secure access to the Principal's Diagnostic Consultation calendar.
                    </p>
                </div>

                {/* CALL TO ACTION: STRIPE REDIRECT */}
                <div className="space-y-6">
                    <button 
                        onClick={() => window.location.href = `/api/stripe/create-checkout?briefId=${briefId}`}
                        className="w-full py-5 bg-stone-900 text-white text-[10px] font-bold uppercase tracking-[0.3em] hover:bg-amber-700 hover:shadow-2xl transition-all flex items-center justify-center gap-3 group rounded-sm shadow-md"
                    >
                        Authorize Phase 01 Audit 
                        <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </button>

                    <div className="flex items-center justify-center gap-8 pt-8 border-t border-stone-100">
                        <div className="flex items-center gap-2 text-stone-400">
                            <ShieldCheck size={14} className="text-amber-600/50" />
                            <span className="text-[9px] uppercase font-bold tracking-[0.1em]">Secure Protocol</span>
                        </div>
                        <div className="flex items-center gap-2 text-stone-400">
                            <FileText size={14} className="text-amber-600/50" />
                            <span className="text-[9px] uppercase font-bold tracking-[0.1em]">Audit Terms Applied</span>
                        </div>
                    </div>
                </div>
            </motion.div>
            
            <p className="mt-12 text-[9px] text-stone-400 uppercase tracking-[0.4em] font-medium">
                Micheals Infrastructure Advisory Consultation Firm | Huntersville, NC
            </p>
        </div>
    );
};

export default IntakeSuccess;